<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UploadInvoiceLines',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const file = ref(null);
  const formId = ref(null);
  const formName = ref('UploadInvoiceLines');
  const resultText = ref(null);
  const downloadTemplateURL = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_3086171180629160_validation = computed(() => [
    'ext:xlsx',
    'required',
  ]);

  const $data = computed(() => ({
    file: file.value,
    formId: formId.value,
    formName: formName.value,
    resultText: resultText.value,
    downloadTemplateURL: downloadTemplateURL.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3086171180629160_validation: b_3086171180629160_validation.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_downloadTemplateURL = () => {
    return $fn.value.urlFor('download_file_template_invoice_lines_upload');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'file' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'resultText' },
      { name: 'downloadTemplateURL' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3086171180629160_validation', nodeId: '3086171180629160' },
    ],
    loaders: {
      formId: $$load_formId,
      downloadTemplateURL: $$load_downloadTemplateURL,
    },
    ux: {},
    dataRefs: {
      file,
      formId,
      formName,
      resultText,
      downloadTemplateURL,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3086171180629160_validation,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'UploadInvoiceLines|Title',
        'Upload invoice lines'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_8eeqq1w3m_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_648747096704084_register_ws7cz3o3l_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'resultLength',
      value: $event.data.response.data ? $event.data.response.data.length : 0,
    };
  };
  const act_648747096704084_setData_ag9qu3h7j_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'resultText',
      value: $fn.value.ngettext(
        'One line has been imported.',
        '${n} lines have been imported.',
        resultLength
      ),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_648747096704084_success_iryfj7nkk_getWhen = async ($event) => {
    return $event.data.resultLength > 0;
  };
  const act_648747096704084_setData_8esjojsml_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'resultText',
      value: $fn.value.gettext('No lines have been imported.'),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const inst_evh_648747096704084_success_33zhgnt2d_getWhen = async ($event) => {
    return !$event.data.resultLength;
  };
  const act_648747096704084_request_y7cbk4jkg_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'upload_invoice_lines',
      data: { file: file.value },
      parameters: {
        invoice_id: props.invoice.id,
      },
      contentType: 'multipart/form-data',
    };
  };
  const act_2434514588044777_closeModal_v7w71y9vk_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_8eeqq1w3m =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_8eeqq1w3m_getActionArgs,
    });
  const inst_evh_7315092382398562_close_9ezxyvcg4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_8eeqq1w3m,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_9ezxyvcg4],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_ebrmn786c =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_yud18xfwy =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ebrmn786c,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_yud18xfwy],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_bk7wmsgxd =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_45ykpoy72 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_bk7wmsgxd,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_45ykpoy72],
    event: 'close',
  });
  const act_648747096704084_register_ws7cz3o3l = new actionTypes.RegisterAction(
    {
      actionArgs: act_648747096704084_register_ws7cz3o3l_getActionArgs,
    }
  );
  const inst_evh_648747096704084_success_7fzj67lie =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_register_ws7cz3o3l,
      event: 'success',
      displayName: 'register',
    });
  const act_648747096704084_setData_ag9qu3h7j = new actionTypes.SetDataAction({
    actionArgs: act_648747096704084_setData_ag9qu3h7j_getActionArgs,
  });
  const inst_evh_648747096704084_success_iryfj7nkk =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_setData_ag9qu3h7j,
      event: 'success',
      when: inst_evh_648747096704084_success_iryfj7nkk_getWhen,
      displayName: 'setData',
    });
  const act_648747096704084_setData_8esjojsml = new actionTypes.SetDataAction({
    actionArgs: act_648747096704084_setData_8esjojsml_getActionArgs,
  });
  const inst_evh_648747096704084_success_33zhgnt2d =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_setData_8esjojsml,
      event: 'success',
      when: inst_evh_648747096704084_success_33zhgnt2d_getWhen,
      displayName: 'setData',
    });
  const act_648747096704084_request_y7cbk4jkg = new actionTypes.RequestAction({
    actionArgs: act_648747096704084_request_y7cbk4jkg_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_648747096704084_success_7fzj67lie,
      inst_evh_648747096704084_success_iryfj7nkk,
      inst_evh_648747096704084_success_33zhgnt2d,
    ],
  });
  const inst_evh_648747096704084_submit_z5rucx4u5 =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_request_y7cbk4jkg,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_648747096704084_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_648747096704084_submit_z5rucx4u5],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_v7w71y9vk =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_v7w71y9vk_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_rp2f81y4s =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_v7w71y9vk,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_rp2f81y4s],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_9ezxyvcg4,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_yud18xfwy,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_45ykpoy72,
    inst_evh_8564662037462133_close,
    inst_evh_648747096704084_success_7fzj67lie,
    inst_evh_648747096704084_success_iryfj7nkk,
    inst_evh_648747096704084_success_33zhgnt2d,
    inst_evh_648747096704084_submit_z5rucx4u5,
    submit: inst_evh_648747096704084_submit_z5rucx4u5,
    inst_evh_648747096704084_submit,
    inst_evh_2434514588044777_clickPreventStop_rp2f81y4s,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_9ezxyvcg4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_9ezxyvcg4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_yud18xfwy = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_yud18xfwy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_45ykpoy72 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_45ykpoy72.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_success_7fzj67lie = async (event, scope) => {
    return await actions.inst_evh_648747096704084_success_7fzj67lie.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_success_iryfj7nkk = async (event, scope) => {
    return await actions.inst_evh_648747096704084_success_iryfj7nkk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_success_33zhgnt2d = async (event, scope) => {
    return await actions.inst_evh_648747096704084_success_33zhgnt2d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_submit_z5rucx4u5 = async (event, scope) => {
    return await actions.inst_evh_648747096704084_submit_z5rucx4u5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_submit = async (event, scope) => {
    return await actions.inst_evh_648747096704084_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_rp2f81y4s = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_rp2f81y4s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="UploadInvoiceLines"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpAlert variant="info" show>
        <p>
          <ItpIcon fa="info-circle"> </ItpIcon>
          <ItpText wrap>
            Számlasorok a következő sablon kitöltésével tölthetők fel:
          </ItpText>
          <ItpLink
            :text="pgettext('UploadInvoiceLines', 'Download template')"
            :href="downloadTemplateURL"
            button
            variant="light"
            use-auth
            force-download
          >
          </ItpLink>
        </p>
      </ItpAlert>
      <hr />
      <div class="container">
        <ItpForm
          :name="formName"
          :id="formId"
          v-if="loaded"
          @submit="evh_648747096704084_submit($event, {})"
        >
          <ItpFormGroup
            name="file"
            :label="pgettext('UploadInvoiceLines', 'Invoice lines file')"
            labelFor="file"
            :description="
              pgettext(
                'UploadInvoiceLines',
                'Please select a filled-in template file.'
              )
            "
            required
          >
            <ItpFormFile
              v-model="file"
              name="file"
              accept=".xlsx"
              required
              :validation="b_3086171180629160_validation"
            >
            </ItpFormFile>
          </ItpFormGroup>
        </ItpForm>
        <ItpAlert :show="resultText && !actions.submit.isRunning">
          <p>
            {{ resultText }}
          </p>
        </ItpAlert>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="formId"
          :text="pgettext('UploadInvoiceLines', 'Upload lines')"
          type="submit"
          variant="primary"
          icon="fa-file-import"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
